import { createTheme } from '@mui/material';
import { AppColors } from './helper/AppColors';


export const theme = createTheme({
	palette: {
		mode: 'light',
		primary: {
			main: '#7F56D9',
			light: '#baa1f0',
			dark: '#512aa3',
		},
		secondary: {
			main: '#000000',
		},
		info: {
			main: '#f3effa',
			light: '#f3effa',
			dark: '#f3effa',
		},
		grey: {
			100: '#DEE1E6',
			200: '#707683',
			300: '#90A0B7',
			400: '#b2b2b2',
		},
		black: {
			100: '#323C47',
			200: '#192A3E',
			700: '#000000',
		},
		blue: {
			50: '#EBF2FC',
			100: '#C2CFE099',
			125: '#DEEAFA',
			150: '#F4F4F6',
			700: '#1890FF',
			900: '#109CF1', // accent blue
		},
	},
	components: {
		MuiButton: {
			variants: [
				{
					props: { size: 'medium' },
					style: { height: 40 },
				},
				{
					props: { color: 'info', variant: 'contained' },
					style: {
						backgroundColor: AppColors.Primary_shade,
						color: AppColors.WHITE,
						border: `1px solid ${AppColors.Primary_shade}`,
						'&:hover': {
							backgroundColor: AppColors.Primary_shade,
							color: AppColors.WHITE,
							opacity: 0.9,
							border: `1px solid ${AppColors.Primary_shade}`,
						}
					},
				},
				{
					props: { color: 'info', variant: 'outlined' },
					style: {
						backgroundColor: AppColors.WHITE,
						color: AppColors.Primary_shade,
						border: `2px solid ${AppColors.Primary_shade}`,
						'&:hover': {
							backgroundColor: AppColors.WHITE,
							color: AppColors.Primary_shade,
							opacity: 0.9,
							border: `2px solid ${AppColors.Primary_shade}`,
						}
					},
				},
			],
			styleOverrides: {
				root: ({ ownerState }) => ({
					textTransform: 'none',
					borderRadius: '8px',
					fontWeight: 500,
					flexShrink: 0,
					...(ownerState.color === 'secondary' && {
						borderColor: 'red',
					}),
				}),
			},
			defaultProps: {
				disableRipple: false,
			},
		},
		MuiLink: {
			defaultProps: {
				underline: 'hover',
				fontSize: 14,
				fontWeight: 500,
			},
			styleOverrides: {
				root: {
					cursor: 'pointer',
					':hover': { color: AppColors.PRIMARY_DARK },
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					'&.MuiPaper-root': { borderRadius: '12px', border: '1px solid #EAECF0' },
					'::before': { display: 'none' },
					'&.Mui-expanded': { margin: 0 },
				},
			},
		},
		MuiButtonBase: {
			defaultProps: {
				TouchRippleProps: { backgroundColor: 'red' },
			},
			styleOverrides: {
				root: {
					'.MuiTouchRipple-child': {
						backgroundColor: '#beafe0',
					},
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					// '& .MuiTableCell-root': {
					// 	paddingTop: 10,
					// 	paddingBottom: 10,
					// },
					textDecoration: 'none',
					// cursor: 'pointer',
				},
			},
		},
		MuiTableBody: {
			styleOverrides: {
				root: {},
			},
		},
		MuiPopover: {
			styleOverrides: {
				paper: { borderRadius: '8px' },
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					padding: 10,
					color: AppColors.BLACK,
					fontSize: 12,
					backgroundColor: '#F3F3F3',
					borderWidth: 1,
					borderStyle: 'solid',
					borderColor: AppColors.BORDER_GREY,
					borderRadius: 10,
					width: 'auto',
					height: 'auto',
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					textDecoration: 'none',
					cursor: 'pointer',
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					margin: 0,
					fontSize: 12,
					color: AppColors.TEXT_TERTIARY,
					marginTop: 4,
				},
			},
		},

		MuiDrawer: {
			styleOverrides: {
				paper: {},
			},
		},

		MuiTypography: {
			styleOverrides: {
				root: {
					fontFamily: 'Poppins',
					lineHeight: 1.5,
				},
				subtitle1: {
					fontSize: 14,
					fontWeight: 500,
					color: AppColors.TEXT_PRIMARY,
				},
				subtitle2: {
					fontSize: 13,
					fontWeight: 400,
					color: AppColors.TEXT_TERTIARY,
				},
				body1: {
					fontSize: 16,
					fontWeight: 600,
					color: AppColors.TEXT_PRIMARY,
				},
				h2: {
					fontSize: 22,
					fontWeight: 600,
					color: AppColors.TEXT_PRIMARY,
				},
				h3: {
					fontSize: 20,
					fontWeight: 600,
					color: AppColors.TEXT_PRIMARY,
				},
			},
		},

		MuiChip: {
			styleOverrides: {
				colorSuccess: {
					backgroundColor: '#ECFDF3',
					border: '1px solid #ABEFC6',
					color: '#067647',
				},
				colorError: {
					backgroundColor: '#FEF3F2',
					border: '1px solid #FECDCA',
					color: '#B42318',
				},
				colorSecondary: {
					backgroundColor: '#fff',
					border: '1px solid',
					borderColor: AppColors.LIGHTER_SHADE,
					color: AppColors.TEXT_SECONDARY,
				},
				colorInfo: {
					backgroundColor: '#B9E6FE',
					border: '1px solid',
					borderColor: '#E9D7FE',
					color: AppColors.TEXT_SECONDARY,
					fontWeight: 600,
				},
			},
		},

		MuiCard: {
			variants: [{ props: { noShadow: true }, style: { boxShadow: 'none' } }],
			styleOverrides: {
				root: (props) => ({
					padding: '16px',
					borderRadius: '12px',
					border: '1px solid',
					borderColor: AppColors.BORDER_GREY,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					width: '100%',
				}),
			},
		},

		MuiModal: {
			variants: [
				{
					props: { type: 'blur' },
					style: { backdropFilter: 'blur(1px)', backgroundColor: 'rgba(0,0,0,0.5)' },
				},
			],
			styleOverrides: {
				root: {
					borderRadius: 0,
				},
			},
		},

		MuiInputLabel: {
			styleOverrides: {
				root: ({ ownerState }) => ({
					color: AppColors.TEXT_SECONDARY,
					fontWeight: 600,
					fontSize: 13,
					...(ownerState.size === 'small' && {
						fontWeight: 400,
						fontSize: 12,
						color: AppColors.TEXT_TERTIARY,
					}),
				}),
			},
		},

		MuiTableContainer: {
			styleOverrides: {
				root: {
					borderRadius: '12px',
					borderWidth: 1,
					borderStyle: 'solid',
					borderColor: AppColors.BORDER_GREY,
				},
			},
		},

		MuiTableCell: {
			styleOverrides: {
				root: {
					borderColor: AppColors.BORDER_GREY,
				},
			},
		},

		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: '#EAECF0',
				},
			},
		},

		MuiAutocomplete: {
			styleOverrides: {
				clearIndicator: {},
				root: {
					borderColor: '#EAECF0',
				},
			},
		},

		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: 0,
					borderRadius: 100,
				},
			},
		},

		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderRadius: '8px',
					border: '1px solid',
					borderColor: AppColors.LIGHTER_SHADE,
				},
				input: {
					fontSize: 16,
					backgroundColor: AppColors.WHITE,
					internal: {
						autofill: {
							selected: {
								backgroundColor: 'white',
							},
						},
					},
				},
			},
		},
	},
	shadows: [
		'none',
		'0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
		'0px 1px 2px rgba(100, 116, 139, 0.12)',
		'0px 1px 4px rgba(100, 116, 139, 0.12)',
		'0px 1px 5px rgba(100, 116, 139, 0.12)',
		'0px 1px 6px rgba(100, 116, 139, 0.12)',
		'0px 2px 6px rgba(100, 116, 139, 0.12)',
		'0px 3px 6px rgba(100, 116, 139, 0.12)',
		'0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
		'0px 5px 12px rgba(100, 116, 139, 0.12)',
		'0px 5px 14px rgba(100, 116, 139, 0.12)',
		'0px 5px 15px rgba(100, 116, 139, 0.12)',
		'0px 6px 15px rgba(100, 116, 139, 0.12)',
		'0px 7px 15px rgba(100, 116, 139, 0.12)',
		'0px 8px 15px rgba(100, 116, 139, 0.12)',
		'0px 9px 15px rgba(100, 116, 139, 0.12)',
		'0px 10px 15px rgba(100, 116, 139, 0.12)',
		'0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
		'0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
		'0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
		'0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
		'0px 25px 50px rgba(100, 116, 139, 0.25)',
		'0px 25px 50px rgba(100, 116, 139, 0.25)',
		'0px 25px 50px rgba(100, 116, 139, 0.25)',
		'0px 25px 50px rgba(100, 116, 139, 0.25)',
	],
	shadowsReverse: [
		'none',
		'0px -1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
		'0px -1px 2px rgba(100, 116, 139, 0.12)',
		'0px -1px 4px rgba(100, 116, 139, 0.12)',
		'0px -1px 5px rgba(100, 116, 139, 0.12)',
		'0px -1px 6px rgba(100, 116, 139, 0.12)',
		'0px -2px 6px rgba(100, 116, 139, 0.12)',
		'0px -3px 6px rgba(100, 116, 139, 0.12)',
		'0px -2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
		'0px -5px 12px rgba(100, 116, 139, 0.12)',
		'0px -5px 14px rgba(100, 116, 139, 0.12)',
		'0px -5px 15px rgba(100, 116, 139, 0.12)',
		'0px -6px 15px rgba(100, 116, 139, 0.12)',
		'0px -7px 15px rgba(100, 116, 139, 0.12)',
		'0px -8px 15px rgba(100, 116, 139, 0.12)',
		'0px -9px 15px rgba(100, 116, 139, 0.12)',
		'0px -10px 15px rgba(100, 116, 139, 0.12)',
		'0px -12px 22px -8px rgba(100, 116, 139, 0.25)',
		'0px -13px 22px -8px rgba(100, 116, 139, 0.25)',
		'0px -14px 24px -8px rgba(100, 116, 139, 0.25)',
		'0px -10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
		'0px -25px 50px rgba(100, 116, 139, 0.25)',
		'0px -25px 50px rgba(100, 116, 139, 0.25)',
		'0px -25px 50px rgba(100, 116, 139, 0.25)',
		'0px -25px 50px rgba(100, 116, 139, 0.25)',
	],
});
